import { isDevMode } from "@/app/common/functions/env/isDevMode";

import { type DeliveryPhaseId } from "../delivery";
import {
  serviceSlugs,
  type CapabilitySlug,
  type IndustrySlug,
  type RichContentBlock,
  type SeoContent,
  type ServiceKind,
  type ServiceSlug,
} from "../contentSchema";

import { service as interactiveVisualizationSystems } from "./records/interactive-visualization-systems";
import { service as configurationManagement } from "./records/configuration-management";
import { service as controlRoomDesign } from "./records/control-room-design";
import { service as fleetTooling } from "./records/fleet-tooling";
import { service as groundedAiWorkflowAutomation } from "./records/grounded-ai-workflow-automation";
import { service as legacyApplicationRefactoring } from "./records/legacy-application-refactoring";
import { service as libraryArchivalSystems } from "./records/library-archival-systems";
import { service as maintenanceSystems } from "./records/maintenance-systems";
import { service as mediaAssetManagement } from "./records/media-asset-management";
import { service as mediaIngestDelivery } from "./records/media-ingest-delivery";
import { service as contentProductionSystems } from "./records/content-production-systems";
import { service as cyberCompliance } from "./records/cyber-compliance";
import { service as cyberDefense } from "./records/cyber-defense";
import { service as crossDomainDataTransfer } from "./records/cross-domain-data-transfer";
import { service as smartForms } from "./records/smart-forms";
import { service as s1000dConversion } from "./records/s1000d-conversion";
import { service as scadaPlcIntegration } from "./records/scada-plc-integration";
import { service as searchRelevanceTuning } from "./records/search-relevance-tuning";
import { service as technicalAudits } from "./records/technical-audits";
import { service as technicalTraining } from "./records/technical-training";
import { service as technicalDocumentation } from "./records/technical-documentation";
import { service as dataStorage } from "./records/data-storage";
import { service as clinicalWorkflowSystems } from "./records/clinical-workflow-systems";
import { service as medicalDeviceInterfaces } from "./records/medical-device-interfaces";
import { service as careCoordinationSystems } from "./records/care-coordination-systems";
import { service as enterpriseDataReconciliation } from "./records/enterprise-data-reconciliation";
import { service as internalPlatformSystems } from "./records/internal-platform-systems";
import { service as cropKnowledgeEngines } from "./records/crop-knowledge-engines";
import { service as spatialFieldPlanning } from "./records/spatial-field-planning";
import { service as cropSuccessionScheduling } from "./records/crop-succession-scheduling";
import { service as nurseryPropagationSystems } from "./records/nursery-propagation-systems";
import { service as agriculturalWorkManagement } from "./records/agricultural-work-management";
import { service as fieldScoutingRecords } from "./records/field-scouting-records";
import { service as agriculturalTelemetrySystems } from "./records/agricultural-telemetry-systems";
import { service as greenhouseAutomationControls } from "./records/greenhouse-automation-controls";
import { service as harvestTraceabilitySystems } from "./records/harvest-traceability-systems";
import { service as ownersEngineering } from "./records/owners-engineering";
import { service as technicalProjectManagement } from "./records/technical-project-management";
import { service as networkAuditMapping } from "./records/network-audit-mapping";
import { service as dcsMigrationEngineering } from "./records/dcs-migration-engineering";
import { service as substationAutomationSystems } from "./records/substation-automation-systems";
import { service as industrialMiningControls } from "./records/industrial-mining-controls";
import { service as emissionsMonitoringSystems } from "./records/emissions-monitoring-systems";
import { service as intelligentDocumentExtraction } from "./records/intelligent-document-extraction";
import { service as industrialAnomalyDetection } from "./records/industrial-anomaly-detection";
import { service as intelligentDiagnosticSystems } from "./records/intelligent-diagnostic-systems";
import { service as agenticCodeIntelligence } from "./records/agentic-code-intelligence";
import { service as multimodalArchiveIndexing } from "./records/multimodal-archive-indexing";
import { service as technicalAiAssistants } from "./records/technical-ai-assistants";

export { serviceSlugs };
export type { ServiceSlug, ServiceKind };

// Which veracity tier a service sits in, per the three tiers defined in
// docs/product/policies/content/text.md §7. This is a claim-safety field, not a
// decoration: it decides how a service may be worded, and it keeps a reader
// from mistaking founder background for General Applications past performance —
// see services-strategy.md §4.6.
type ServiceTier = "we-do" | "experience" | "exploring";

// Content lifecycle states for the service layer.
//
// Diverges deliberately from the blog's two-state model (idea -> published):
// services are released as a layer, not one by one as they are written. An
// explicit batch-release decision gates published status (services-strategy.md
// §5).
//
// The pipeline has four states:
//
//   idea       Named only: slug, title, capability/industry ties, summary.
//              No body, deliverables, fit, or questions yet.
//
//   draft      Written: engineering list and phases present. Reviewable
//              in dev; excluded from production lists, routes, and sitemaps.
//
//   ready      Proof gate met: at least two non-draft blog posts exist that
//              the service page can point to as evidence. Complete and
//              reviewed; awaiting the batch-release decision.
//
//   published  Released: listed on /services and on related capability and
//              industry pages, routed, prerendered, and included in the sitemap.
//
// A ready service differs from a draft only by meeting the proof gate. Both
// remain dev-only until an explicit release.
type ServiceStatus = "idea" | "draft" | "ready" | "published";

// Order in which lifecycle states appear in review lists. Ready work sorts
// first so it is seen first during editorial review; idea sorts last.
const serviceReviewPriority: Record<ServiceStatus, number> = {
  ready: 3,
  draft: 2,
  idea: 1,
  published: 0,
};

// Distinct block types for service bodies. Reuses the content schema's
// rich-block vocabulary without pulling in blog-specific concerns (code tabs,
// figures with captions, pull quotes).
type ServiceContentBlock = Extract<
  RichContentBlock,
  { type: "paragraph" } | { type: "heading" } | { type: "list" }
>;

// Qualification criteria for a service. Both sides are required on any
// written service: the "when this fits" half qualifies the buyer, and the
// "when this is the wrong fit" half protects the firm from engagements it
// should not take. A service page with only the positive half is marketing
// copy, not engineering qualification.
type ServiceFit = {
  good: string[];
  poor: string[];
};

// How a service lands in one shared delivery phase.
//
// Delivery phases (Study -> Initial Design -> Detailed Design -> Implementation)
// are defined by docs/product/design/pages/delivery.md and shared across the
// whole firm. A service does not invent its own lifecycle; it describes what it
// produces, asks, and qualifies for at each stage of the existing one.
export type ServicePhaseDetail = {
  // One sentence: what this phase accomplishes for this specific service.
  focus: string;

  // The concrete deliverables this phase produces for this service.
  produces: string[];

  // Study only: the qualification criteria that decide whether to proceed.
  fit?: ServiceFit;
};

export type ServiceRecord = SeoContent & {
  slug: ServiceSlug;
  title: string;

  // Short label for space-constrained contexts (chips, review lists).
  // Defaults to title when omitted.
  shortTitle?: string;

  // Architectural classification: horizontal capability discipline vs. standalone domain vertical
  kind: ServiceKind;

  // Claim boundary for this service — see text.md §7 and
  // services-strategy.md §4.6.
  tier: ServiceTier;

  // Editorial lifecycle state — see services-strategy.md §5.
  status: ServiceStatus;

  // One-paragraph summary of what the service delivers and when it fits.
  // Used in meta descriptions, index listings, and the page's opening lead.
  summary: string;

  // Primary image for the service hero. Uses the same responsive WebP/AVIF
  // contract as capability and industry heroes.
  image?: string;

  // Capabilities this service draws on.
  relatedCapabilitySlugs: CapabilitySlug[];

  // Industries where this service applies.
  relatedIndustrySlugs: IndustrySlug[];

  // Published blog posts that serve as proof or technical deep-dives for this
  // service. Each slug must resolve to a real BlogPostRecord in content/blog/.
  relatedPostSlugs?: string[];

  // What has actually been engineered in this service.
  //
  // Written as task-level engineering realities — "Multi-master replication
  // topologies", "S1000D issue-to-issue transformation pipelines", "STIG
  // benchmark automation" — not marketing promises. Displayed directly under
  // the veracity tier heading, which grounds the whole list in the appropriate
  // claim boundary: work GA does today, or work drawn from prior founder
  // engineering experience.
  //
  // Required on any service in `draft`, `ready`, or `published` state. The
  // heading is derived from `tier` via `serviceTierHeadings` to prevent a reader
  // taking prior work for company past performance. text.md §7 warns against
  // unqualified headings like "Relevant Experience", so that heading is
  // derived from `tier` rather than authored per record.
  engineering?: string[];

  // The variable middle of the page. Everything that is genuinely specific to
  // this service's engineering lives here rather than in a fixed field.
  body?: ServiceContentBlock[];

  // How this service lands in each shared delivery phase.
  phases?: Partial<Record<DeliveryPhaseId, ServicePhaseDetail>>;
};

// Reader-facing wording for each tier, taken from the "Preferred wording"
// column of text.md §7 rather than invented here.
export const serviceTierLabels: Record<ServiceTier, string> = {
  "we-do": "Current capability",
  experience: "Founder experience",
  exploring: "In development",
};

export const serviceTierHeadings: Record<ServiceTier, string> = {
  "we-do": "Current Capability",
  experience: "Prior Engineering Experience",
  exploring: "In Development",
};

export const serviceTierStatements: Record<ServiceTier, string> = {
  "we-do": "Work General Applications designs and builds today.",
  experience:
    "Drawn from prior engineering experience across employment, consulting, and independent product work — not General Applications past performance.",
  exploring:
    "Capability actively being developed, with working prototypes rather than a delivery record.",
};

export const servicesIndexPage = {
  seoTitle: "Services | Specific Engineering Work We Take On",
  seoDescription:
    "Specific, nameable engineering work across technical publication conversion, search and retrieval, control-system integration, compliance tooling, and application modernization.",
  headline:
    "Capabilities name the discipline. **These are the specific pieces of work.**",
  heroCopy: [
    "A capability names an area of engineering practice, and an industry names the environment it has to survive in. This page sits between them: the concrete, nameable work those two turn into, each one scoped tightly enough to put on a proposal and grounded in a particular standard, protocol, or system.",
    "Each service page states how that work is grounded — whether it is work General Applications does today, work drawn from prior founder engineering experience, or capability actively being developed.",
  ],
} as const;

export const services: ServiceRecord[] = [
  s1000dConversion,
  scadaPlcIntegration,
  searchRelevanceTuning,
  libraryArchivalSystems,
  mediaAssetManagement,
  contentProductionSystems,
  mediaIngestDelivery,
  groundedAiWorkflowAutomation,
  controlRoomDesign,
  configurationManagement,
  maintenanceSystems,
  cyberCompliance,
  cyberDefense,
  crossDomainDataTransfer,
  legacyApplicationRefactoring,
  interactiveVisualizationSystems,
  technicalAudits,
  technicalTraining,
  dataStorage,
  fleetTooling,
  smartForms,
  technicalDocumentation,
  clinicalWorkflowSystems,
  medicalDeviceInterfaces,
  careCoordinationSystems,
  enterpriseDataReconciliation,
  internalPlatformSystems,
  cropKnowledgeEngines,
  spatialFieldPlanning,
  cropSuccessionScheduling,
  nurseryPropagationSystems,
  agriculturalWorkManagement,
  fieldScoutingRecords,
  agriculturalTelemetrySystems,
  greenhouseAutomationControls,
  harvestTraceabilitySystems,
  ownersEngineering,
  technicalProjectManagement,
  networkAuditMapping,
  dcsMigrationEngineering,
  substationAutomationSystems,
  industrialMiningControls,
  emissionsMonitoringSystems,
  intelligentDocumentExtraction,
  industrialAnomalyDetection,
  intelligentDiagnosticSystems,
  agenticCodeIntelligence,
  multimodalArchiveIndexing,
  technicalAiAssistants,
];

export function getServiceBySlug(slug: string): ServiceRecord | undefined {
  return services.find((service) => service.slug === slug);
}

export function isPublicService(service: ServiceRecord): boolean {
  return service.status === "published";
}

export function isPreviewableService(service: ServiceRecord): boolean {
  return isDevMode() || isPublicService(service);
}

export function isListableService(service: ServiceRecord): boolean {
  return isDevMode() || isPublicService(service);
}

export function sortServicesByStatus(
  serviceRecords: readonly ServiceRecord[],
): ServiceRecord[] {
  return [...serviceRecords].sort(
    (left, right) =>
      serviceReviewPriority[right.status] - serviceReviewPriority[left.status],
  );
}

export function getServicesByCapability(slug: CapabilitySlug): ServiceRecord[] {
  return sortServicesByStatus(
    services.filter(
      (service) =>
        isListableService(service) &&
        service.relatedCapabilitySlugs.includes(slug),
    ),
  );
}

export function getServicesByIndustry(slug: IndustrySlug): ServiceRecord[] {
  return sortServicesByStatus(
    services.filter(
      (service) =>
        isListableService(service) &&
        service.relatedIndustrySlugs.includes(slug),
    ),
  );
}
